import {
  FormControl,
  FormHelperText,
  FormLabel,
  Input,
  InputGroup,
  InputRightAddon,
  Select,
  SimpleGrid,
  Stack,
  Text,
} from "@chakra-ui/react";
import { forwardRef, useImperativeHandle, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { v4 as uuid } from "uuid";
import {
  ContractNature,
  Regulation,
  ServiceCategory,
  UtilityActivity,
  type MarketDialogueDto,
  type MarketDialogueQuestionDto,
  type UpdateMarketDialogueRequest,
  useUpdateMarketDialogueMutation,
} from "../../autogen/bff-api";
import { CpvSelector } from "../../common/cpv/CpvSelector";
import { useApiError } from "../../common/errors/useApiError";
import { ThemedDatePicker } from "../../common/input/DateTimeSelectors/ThemedDatePicker";
import { ThemedDateTimePicker } from "../../common/input/DateTimeSelectors/ThemedDateTimePicker";
import { NumericInput } from "../../common/input/NumericInput";
import { RadioOptionGroup } from "../../common/input/RadioOptionGroup";
import { TipTap } from "../../common/input/TipTap/TipTap";
import { useDebouncedSave } from "../../common/input/useDebouncedSave";
import { firstTooLongQuestion } from "./questionLimits";
import { CONTRACT_NATURE_OPTIONS } from "../../common/procurement/publicProcurementOptions";
import { SERVICE_CATEGORIES, serviceCategoryLabel } from "../../common/procurement/serviceCategory";
import { isChangedElsewhere } from "./changedElsewhere";
import { MarketDialogueFormSection } from "./MarketDialogueFormSection";
import { MarketDialogueQuestions } from "./MarketDialogueQuestions";

const tomorrow = () => {
  const date = new Date();
  date.setDate(date.getDate() + 1);
  return date;
};

const ANNOUNCED_ON_DOFFIN = "Announce on Doffin";
const ONLY_INVITED = "Only invited suppliers";
// t('Announce on Doffin') t('Only invited suppliers') t('Every supplier can find and answer the market dialogue. You can invite suppliers as well.') t('Nothing is announced. Only the suppliers you invite can see and answer the market dialogue.')
const REACH_OPTIONS = [
  {
    value: ANNOUNCED_ON_DOFFIN,
    descriptionKey: "Every supplier can find and answer the market dialogue. You can invite suppliers as well.",
  },
  {
    value: ONLY_INVITED,
    descriptionKey: "Nothing is announced. Only the suppliers you invite can see and answer the market dialogue.",
  },
] as const;

const REGULATIONS = [Regulation.Foa, Regulation.Forsyningsforskriften];
const UTILITY_ACTIVITIES = Object.values(UtilityActivity);

type MarketDialogueContent = Omit<UpdateMarketDialogueRequest, "contentRevision">;

export const contentOf = (dialogue: MarketDialogueDto): MarketDialogueContent => ({
  title: dialogue.title,
  description: dialogue.description ?? null,
  questions: dialogue.questions,
  answerDeadline: dialogue.answerDeadline ?? null,
  regulation: dialogue.regulation ?? null,
  contractNature: dialogue.contractNature ?? null,
  serviceCategory: dialogue.serviceCategory ?? null,
  estimatedValueNok: dialogue.estimatedValueNok ?? null,
  cpvCodes: dialogue.cpvCodes,
  utilityActivity: dialogue.utilityActivity ?? null,
  expectedContractNoticeDate: dialogue.expectedContractNoticeDate ?? null,
  announceOnDoffin: dialogue.announceOnDoffin,
});

export interface MarketDialogueFormHandle {
  contentRevision: () => number;
  saveAgain: () => Promise<void>;
}

export type MarketDialogueFormStep = "basis" | "questions" | "reachMarket";

interface Props {
  step: MarketDialogueFormStep | null;
  dialogue: MarketDialogueDto;
  changedElsewhere: boolean;
  onChangedElsewhere: () => void;
  onSaving: () => void;
  onSaved: (saved: MarketDialogueDto) => void;
  onSaveFailed: () => void;
  onHeldBack: (tooLongQuestion: number | null) => void;
}

export const MarketDialogueForm = forwardRef<MarketDialogueFormHandle, Props>(function MarketDialogueForm(
  { step, dialogue, changedElsewhere, onChangedElsewhere, onSaving, onSaved, onSaveFailed, onHeldBack },
  ref,
) {
  const { t } = useTranslation();
  const { trigger } = useApiError();
  const [update] = useUpdateMarketDialogueMutation();
  const [content, setContent] = useState<MarketDialogueContent>(() => contentOf(dialogue));
  const contentRevision = useRef(dialogue.contentRevision);
  const stopped = useRef(false);
  const previousSave = useRef<Promise<void>>(Promise.resolve());
  const latestChange = useRef(0);
  const latestContent = useRef(content);

  const send = async (next: MarketDialogueContent, changeNumber: number) => {
    if (stopped.current || changedElsewhere) return;
    const isLatest = () => changeNumber === latestChange.current;
    try {
      const saved = await update({
        marketDialogueId: dialogue.id,
        updateMarketDialogueRequest: { ...next, contentRevision: contentRevision.current },
      }).unwrap();
      contentRevision.current = saved.contentRevision;
      if (isLatest()) onSaved(saved);
    } catch (e) {
      if (isChangedElsewhere(e)) {
        stopped.current = true;
        onChangedElsewhere();
      } else {
        trigger(e);
        if (isLatest()) onSaveFailed();
      }
    }
  };

  const save = useDebouncedSave((next: MarketDialogueContent, changeNumber: number) => {
    previousSave.current = previousSave.current.then(() => send(next, changeNumber));
    return previousSave.current;
  }, 500);

  const saveLatest = (next: MarketDialogueContent) => {
    latestContent.current = next;
    latestChange.current += 1;
    onSaving();
    return save(next, latestChange.current);
  };

  const change = (patch: Partial<MarketDialogueContent>) => {
    if (changedElsewhere) return;
    const next = { ...content, ...patch };
    setContent(next);
    const tooLongQuestion = firstTooLongQuestion(next.questions ?? []);
    onHeldBack(tooLongQuestion);
    if (tooLongQuestion !== null) {
      save.cancel();
      return;
    }
    void saveLatest(next);
  };

  useImperativeHandle(ref, () => ({
    contentRevision: () => contentRevision.current,
    saveAgain: async () => {
      void saveLatest(latestContent.current);
      await save.flush();
    },
  }));

  const changeQuestions = (questions: MarketDialogueQuestionDto[]) =>
    change({ questions: questions.map((question) => ({ ...question, id: question.id ?? uuid() })) });

  if (step === null) return null;

  return (
    <Stack as="fieldset" disabled={changedElsewhere} spacing="6" minW="0">
      {step === "basis" && (
        <>
          <MarketDialogueFormSection heading={t("General information")}>
            <FormControl isRequired>
              <FormLabel>{t("Title")}</FormLabel>
              <Input value={content.title} onChange={(e) => change({ title: e.target.value })} />
            </FormControl>
            <FormControl isRequired>
              <FormLabel>{t("Description")}</FormLabel>
              <TipTap
                content={content.description ?? ""}
                editable={!changedElsewhere}
                debounceWait={0}
                onUpdate={(value) => change({ description: value })}
              />
              <FormHelperText>{t("Describe the planned procurement and what you want input on")}</FormHelperText>
            </FormControl>
          </MarketDialogueFormSection>
          <MarketDialogueFormSection heading={t("Classification")}>
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing="4">
              {dialogue.canAnnounceOnDoffin && (
                <>
              <FormControl isRequired>
                <FormLabel>{t("Regulation")}</FormLabel>
                <Select
                  placeholder={t("Choose") ?? undefined}
                  value={content.regulation ?? ""}
                  onChange={(e) => {
                    const regulation = (e.target.value || null) as Regulation | null;
                    change({
                      regulation,
                      utilityActivity: regulation === Regulation.Forsyningsforskriften ? content.utilityActivity : null,
                    });
                  }}
                >
                  {REGULATIONS.map((regulation) => (
                    <option key={regulation} value={regulation}>
                      {t(regulation)}
                    </option>
                  ))}
                </Select>
              </FormControl>
              {content.regulation === Regulation.Forsyningsforskriften && (
                <FormControl isRequired>
                  <FormLabel>{t("Utility activity")}</FormLabel>
                  <Select
                    placeholder={t("Choose") ?? undefined}
                    value={content.utilityActivity ?? ""}
                    onChange={(e) => change({ utilityActivity: (e.target.value || null) as UtilityActivity | null })}
                  >
                    {UTILITY_ACTIVITIES.map((activity) => (
                      <option key={activity} value={activity}>
                        {t(activity)}
                      </option>
                    ))}
                  </Select>
                </FormControl>
              )}
                </>
              )}
              <FormControl isRequired>
                <FormLabel>{t("What does the procurement concern?")}</FormLabel>
                <Select
                  placeholder={t("Choose") ?? undefined}
                  value={content.contractNature ?? ""}
                  onChange={(e) => {
                    const contractNature = (e.target.value || null) as ContractNature | null;
                    change({
                      contractNature,
                      serviceCategory: contractNature === ContractNature.Services ? content.serviceCategory : null,
                    });
                  }}
                >
                  {CONTRACT_NATURE_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {t(option.value)}
                    </option>
                  ))}
                </Select>
              </FormControl>
              {dialogue.canAnnounceOnDoffin && content.contractNature === ContractNature.Services && (
                <FormControl isRequired>
                  <FormLabel>{t("What kind of service is the contract?")}</FormLabel>
                  <Select
                    placeholder={t("Choose") ?? undefined}
                    value={content.serviceCategory ?? ""}
                    onChange={(e) => change({ serviceCategory: (e.target.value || null) as ServiceCategory | null })}
                  >
                    {SERVICE_CATEGORIES.map((category) => (
                      <option key={category} value={category}>
                        {serviceCategoryLabel(t, category, content.regulation === Regulation.Forsyningsforskriften)}
                      </option>
                    ))}
                  </Select>
                  <FormHelperText>{t("Special services and health and social services have thresholds of their own")}</FormHelperText>
                </FormControl>
              )}
              <FormControl isRequired>
                <FormLabel>{t("Estimated value")}</FormLabel>
                <InputGroup>
                  <NumericInput
                    numericValue={content.estimatedValueNok ?? null}
                    onNumericChange={(value) => change({ estimatedValueNok: value })}
                  />
                  <InputRightAddon>{t("NOK excl. VAT")}</InputRightAddon>
                </InputGroup>
                {dialogue.canAnnounceOnDoffin && content.announceOnDoffin && (
                  <FormHelperText>{t("Decides whether the notice goes out below or above the EEA threshold")}</FormHelperText>
                )}
              </FormControl>
            </SimpleGrid>
            {dialogue.canAnnounceOnDoffin && (
              <FormControl isRequired>
                <FormLabel>{t("CPV codes")}</FormLabel>
                <CpvSelector
                  value={content.cpvCodes ?? []}
                  onChange={(cpvCodes) => change({ cpvCodes })}
                  isDisabled={changedElsewhere}
                />
              </FormControl>
            )}
          </MarketDialogueFormSection>
        </>
      )}
      {step === "questions" && (
        <MarketDialogueFormSection heading={t("Questions to the suppliers")}>
            <FormControl isRequired>
              <FormLabel srOnly>{t("Questions to the suppliers")}</FormLabel>
              <MarketDialogueQuestions questions={content.questions ?? []} onChange={changeQuestions} />
            </FormControl>
        </MarketDialogueFormSection>
      )}
      {step === "reachMarket" && (
        <MarketDialogueFormSection heading={t("How suppliers learn about it")}>
          {dialogue.canAnnounceOnDoffin ? (
            <RadioOptionGroup
              options={REACH_OPTIONS}
              value={content.announceOnDoffin ? ANNOUNCED_ON_DOFFIN : ONLY_INVITED}
              onChange={(value) => change({ announceOnDoffin: value === ANNOUNCED_ON_DOFFIN })}
            />
          ) : (
            dialogue.buyerLegalTypeMissing ? (
              <Text fontSize="sm" color="smMuted" data-testid="buyer-legal-type-missing-reach">
                {t(
                  "Your organisation has no «Buyer legal type», so the market dialogue can only open to the suppliers you invite. SourceMagnet sets the legal type: to announce on Doffin, ask support through «Help» in the menu.",
                )}
              </Text>
            ) : (
              <Text fontSize="sm" color="smMuted" data-testid="invitation-only-reach">
                {t(
                  "Your organisation is not bound by the procurement rules, so the market dialogue is not announced on Doffin. It opens to the suppliers you invite, and only they can read and answer it.",
                )}
              </Text>
            )
          )}
        </MarketDialogueFormSection>
      )}
      {step === "reachMarket" && (
        <MarketDialogueFormSection heading={t("Deadlines")}>
            <FormControl isRequired>
              <FormLabel>{t("Answer deadline")}</FormLabel>
              <ThemedDateTimePicker
                date={content.answerDeadline ?? null}
                onChange={(iso) => change({ answerDeadline: iso ?? null })}
                minDate={new Date()}
              />
              <FormHelperText>{t("Suppliers can answer until this time")}</FormHelperText>
            </FormControl>
            {content.announceOnDoffin && (
            <FormControl isRequired={dialogue.expectedContractNoticeDateRequired}>
              <FormLabel>{t("When do you expect to announce the competition?")}</FormLabel>
              <ThemedDatePicker
                date={content.expectedContractNoticeDate ?? null}
                onChange={(date) => change({ expectedContractNoticeDate: date ?? null })}
                minDate={tomorrow()}
                isClearable
              />
              <FormHelperText>{t("An estimate is enough. Doffin shows the date to suppliers in the notice.")}</FormHelperText>
            </FormControl>
            )}
        </MarketDialogueFormSection>
      )}
    </Stack>
  );
});
